import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import {
  AdvisorProfileErrorState,
  AdvisorProfileSkeleton,
  AdvisorProfileView,
} from '@/shared/components/advisor-profile';
import { useAuth } from '../../authentication/hooks/useAuth';
import { useWorkspace } from '@/features/workspace-sub-delegation';
import { useFinancialAdvisorProfile } from '../hooks/useFinancialAdvisorProfile';
import { ReferAdvisorModal } from './ReferAdvisorModal';

type ApiError = {
  statusCode?: number;
  response?: { status?: number };
};

const getErrorStatus = (error: unknown) => {
  const apiError = error as ApiError;
  return apiError.statusCode ?? apiError.response?.status;
};

export const AdvisorProfileDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  // Delegates act under the workspace owner's role + WORKSPACE_OPERATIONS grant.
  const { effectiveRole, hasWorkspacePermission } = useWorkspace();
  const [isReferModalOpen, setIsReferModalOpen] = useState(false);
  const { data: profile, isLoading, isError, error, refetch } = useFinancialAdvisorProfile(id);

  if (isLoading) {
    return <AdvisorProfileSkeleton fullPage />;
  }

  if (isError || !profile) {
    const isNotFound = getErrorStatus(error) === 404;
    return <AdvisorProfileErrorState isNotFound={isNotFound} onRetry={refetch} fullPage />;
  }

  const role = effectiveRole ?? user?.role;
  const canReferAdvisor =
    role === 'SERVICE_PROVIDER' && hasWorkspacePermission('WORKSPACE_OPERATIONS');

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <Link to="/search-providers" className="inline-flex items-center gap-2 text-[#026857]">
            <ChevronLeft className="w-5 h-5" />
            Back to Results
          </Link>
        </div>
      </div>

      <main className="container mx-auto max-w-6xl px-4 py-8 space-y-6">
        <AdvisorProfileView
          profile={profile}
          showServiceProviderActions={canReferAdvisor}
          onReferAdvisor={() => setIsReferModalOpen(true)}
        />
        {canReferAdvisor && (
          <ReferAdvisorModal
            open={isReferModalOpen}
            financialAdvisorId={profile.id}
            advisorName={`${profile.user.firstName} ${profile.user.lastName ?? ''}`.trim()}
            onOpenChange={setIsReferModalOpen}
          />
        )}
      </main>
    </div>
  );
};
